import { z } from "zod";
import {
  CREATOR_ENRICHMENT_STATUSES,
  CREATOR_STATUSES,
  OPPORTUNITY_STATUSES,
} from "@artha/types";

export const EnrichmentRunBodySchema = z.object({
  force: z.boolean().optional(),
});

export const EnrichmentStatusSchema = z.object({
  status: z.enum(CREATOR_ENRICHMENT_STATUSES),
  lastRunAt: z.string().datetime().optional(),
  completedAt: z.string().datetime().optional(),
  failReason: z.string().optional(),
  data: z
    .object({
      bioGenerated: z.string().optional(),
      nicheSuggestions: z.array(z.string()).optional(),
      jobKeywords: z.array(z.string()).optional(),
      followersTotal: z.number().optional(),
      opportunityStatus: z.enum(OPPORTUNITY_STATUSES).optional(),
      decisionReason: z.string().optional(),
    })
    .optional(),
});

export type EnrichmentRunBodyDto = z.infer<typeof EnrichmentRunBodySchema>;
export type EnrichmentStatusDto = z.infer<typeof EnrichmentStatusSchema>;

export const NicheRequestSchema = z.object({
  profileUrl: z.string().url().optional(),
  force: z.boolean().optional(),
});

export const NicheResponseSchema = z.object({
  nicheTags: z.array(z.string()),
  jobKeywords: z.array(z.string()).optional(),
  followers: z.number().nullish(),
});

export const ManualNicheRequestSchema = z.object({
  nicheTags: z.array(z.string()),
  jobKeywords: z.array(z.string()).optional(),
});

export const PredictKeywordsRequestSchema = z.object({
  nicheTags: z.array(z.string()).min(1, "nicheTags are required"),
});

export const ProfileBuildRequestSchema = z.object({
  profileUrl: z.string().url(),
  force: z.boolean().optional(),
});

export const ProfileBuildResponseSchema = z.object({
  bio: z.string(),
  greetingMessage: z.string().optional(),
  nicheTags: z.array(z.string()),
  jobKeywords: z.array(z.string()).optional(),
  fit: z.object({
    approved: z.boolean(),
    confidence: z.number().min(0).max(1),
    reasons: z.array(z.string()).optional(),
  }),
  followersTotal: z.number().optional(),
  avatarUrl: z.string().url().optional(),
});

export type NicheRequestDto = z.infer<typeof NicheRequestSchema>;
export type NicheResponseDto = z.infer<typeof NicheResponseSchema>;
export type ManualNicheRequestDto = z.infer<typeof ManualNicheRequestSchema>;
export type PredictKeywordsRequestDto = z.infer<typeof PredictKeywordsRequestSchema>;
export type ProfileBuildRequestDto = z.infer<typeof ProfileBuildRequestSchema>;
export type ProfileBuildResponseDto = z.infer<typeof ProfileBuildResponseSchema>;

export const OpportunitySchema = z.object({
  id: z.string(),
  title: z.string(),
  category: z.string().optional(),
  tags: z.array(z.string()).optional(),
  fitScore: z.number().min(0).max(1).optional(),
  reason: z.string().optional(),
  cpa: z.number().optional(),
});

export const EarningTierSchema = z.object({
  frequency: z.string(),
  weeklyAmount: z.number(),
  monthlyAmount: z.number(),
});

export const EarningPotentialSchema = z.object({
  weeklyClicks: z.number(),
  weeklyApplications: z.number(),
  weeklyRevenue: z.number(),
  monthlyEstimate: z.number(),
  tiers: z.array(EarningTierSchema),
});

export const OpportunitiesRequestSchema = z.object({
  profileUrl: z.string().url().optional(),
  force: z.boolean().optional(),
});

export const OpportunitiesResponseSchema = z.object({
  jobs: z.array(OpportunitySchema),
  earningPotential: EarningPotentialSchema.optional(),
});

// New Step-based API Schemas
export const ReadProfileRequestSchema = z.object({
  profileUrl: z.string().url(),
  skipThreshold: z.boolean().optional(),
});

export const ReadProfileResponseSchema = z.object({
  followers: z.number(),
  provider: z.string(),
  profileUrl: z.string(),
  avatarUrl: z.string().optional(),
  username: z.string().optional(),
});

export const IdentifyNicheRequestSchema = z.object({
  force: z.boolean().optional(),
});

export const IdentifyNicheResponseSchema = z.object({
  nicheTags: z.array(z.string()),
  jobKeywords: z.array(z.string()),
  isValidTaxonomy: z.boolean(),
  confidence: z.number().min(0).max(1).optional(),
  confidenceReasoning: z.string().optional(),
  status: z.enum(CREATOR_STATUSES).optional(),
});

export const OpportunityCheckResponseSchema = z.object({
  status: z.enum(OPPORTUNITY_STATUSES),
  jobCount: z.number(),
  matchPercentage: z.number().optional(),
  matchCount: z.number(),
  reason: z.string().optional(),
  profileStatus: z.enum(CREATOR_STATUSES).optional(),
});

// Type exports for new schemas
export type ReadProfileRequestDto = z.infer<typeof ReadProfileRequestSchema>;
export type ReadProfileResponseDto = z.infer<typeof ReadProfileResponseSchema>;
export type IdentifyNicheRequestDto = z.infer<typeof IdentifyNicheRequestSchema>;
export type IdentifyNicheResponseDto = z.infer<typeof IdentifyNicheResponseSchema>;

export const EnrichmentSummarySchema = z.object({
  name: z.string().optional(),
  avatarUrl: z.string().url().optional(),
  profileUrl: z.string().url().optional(),
  followersTotal: z.number().optional(),
  followersByPlatform: z.record(z.string(), z.number()).optional(),
  primaryPlatform: z.string().optional(),
  nicheTags: z.array(z.string()).optional(),
  jobKeywords: z.array(z.string()).optional(),
  headline: z.string().optional(),
  bio: z.string().optional(),
  greetingMessage: z.string().optional(),
  username: z.string().optional(),
  earningPotential: EarningPotentialSchema.optional(),
  opportunityStatus: z.enum(OPPORTUNITY_STATUSES).optional(),
  decisionReason: z.string().optional(),
  rejectedAt: z.string().datetime().optional(),
  appealRequestedAt: z.string().datetime().optional(),
  nicheConfidence: z.number().min(0).max(1).optional(),
  confidenceReasoning: z.string().optional(),
});

export type OpportunitiesRequestDto = z.infer<typeof OpportunitiesRequestSchema>;
export type OpportunitiesResponseDto = z.infer<typeof OpportunitiesResponseSchema>;
export type OpportunityCheckResponseDto = z.infer<typeof OpportunityCheckResponseSchema>;
export type EnrichmentSummaryDto = z.infer<typeof EnrichmentSummarySchema>;

// Appeal Request Schema
export const AppealRequestResponseSchema = z.object({
  success: z.boolean(),
  appealRequestedAt: z.string().datetime(),
});

export type AppealRequestResponseDto = z.infer<typeof AppealRequestResponseSchema>;
